import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { listNotificationProblems, apiErrorMessage } from "../../api/assessmentPayments";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import useColumnVisibility from "./useColumnVisibility";
import ColumnsMenuButton from "./ColumnsMenuButton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";

const ACCENT = "#17479e";
const CARD_SHADOW = "0 6px 20px rgba(23,71,158,0.08), 0 1px 3px rgba(0,0,0,0.04)";

const EVENT_LABELS = {
  PURCHASE_SUCCESS: "Purchase confirmation",
  PAYMENT_FAILED: "Payment failed",
  EXPIRY_3D: "Expiry in 3 days",
  EXPIRED: "Expired",
};

const CHANNEL_LABELS = { MAIL: "Mail", WHATSAPP: "WhatsApp" };

// FAILED = the send errored 3 times and was given up on. SKIPPED = there was nothing to send to or
// nothing configured to send with (the reason is in the Reason column), so it is often expected.
const STATUS_CFG = {
  FAILED: { label: "Failed", color: "#c62828", bg: "#ffebee" },
  SKIPPED: { label: "Skipped", color: "#ef6c00", bg: "#fff3e0" },
};

const KIND_LABELS = { CATEGORY: "Course", SERIES: "Test series" };

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const columns = [
  { key: "createdAt", label: "Queued" },
  { key: "eventType", label: "Message" },
  { key: "channel", label: "Channel" },
  { key: "studentUserId", label: "Student ID" },
  { key: "ref", label: "Purchase" },
  { key: "status", label: "Status", align: "center" },
  { key: "attempts", label: "Attempts", align: "center" },
  { key: "lastError", label: "Reason" },
];

function StatusChip({ value }) {
  const cfg = STATUS_CFG[String(value ?? "").toUpperCase()] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: "0.68rem", color: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function renderCell(key, row) {
  switch (key) {
    case "createdAt":
      return formatDateTime(row.createdAt);
    case "eventType":
      return EVENT_LABELS[row.eventType] ?? row.eventType ?? "—";
    case "channel":
      return CHANNEL_LABELS[row.channel] ?? row.channel ?? "—";
    case "studentUserId":
      return row.studentUserId ?? "—";
    case "ref":
      return `${KIND_LABELS[row.refKind] ?? row.refKind ?? ""} #${row.refId ?? "—"}`.trim();
    case "status":
      return <StatusChip value={row.status} />;
    case "attempts":
      return row.attempts ?? 0;
    case "lastError":
      return row.lastError ? (
        <Tooltip title={row.lastError} arrow>
          <Typography
            sx={{ fontSize: "0.8rem", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {row.lastError}
          </Typography>
        </Tooltip>
      ) : (
        "—"
      );
    default:
      return "—";
  }
}

// Read-only list of invoice / mail / WhatsApp messages that did not go out. Shown under the
// transactions table on the Payments page so a missing confirmation can be traced.
function AssessmentNotificationIssues() {
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { visibleColumns, toggleColumn, visibleColumnDefs } = useColumnVisibility(
    "assessmentNotificationIssuesHiddenColumns",
    columns,
  );

  useEffect(() => {
    setLoading(true);
    listNotificationProblems({ page, size: rowsPerPage })
      .then((data) => {
        setRows(data?.content || []);
        setTotalElements(data?.totalElements ?? 0);
      })
      .catch((err) => {
        setRows([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load notification issues"));
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 2, mt: 3, mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#1a1a2e", mb: 0.5 }}>
            Notification Issues
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            Purchase and expiry messages that failed or were skipped. A skipped message usually means the student has no
            email or phone number, or the WhatsApp template is not configured.
          </Typography>
        </Box>
        <ColumnsMenuButton allColumns={columns} visibleColumns={visibleColumns} onToggle={toggleColumn} />
      </Box>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: CARD_SHADOW, borderRadius: 3, border: "1px solid #f0f1f6" }}>
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    sx={{
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No notification issues" hint="Every queued message has gone out." />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.align} sx={{ padding: "10px 12px" }}>
                        {renderCell(c.key, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>
    </>
  );
}

export default AssessmentNotificationIssues;
